import { ProductDynamicAttributesType } from '../Product';
import { Morphologies } from 'settings/productV2';

export type ProductStaticDataType = {
  breadcrumb: ProductBreadcrumbType[];
  product: ProductType;
  variants: ProductVariantsColorsType;
  sizes: string[];
  cups: string[];
  variantsSkus: string[];
};

export type ProductBreadcrumbType = {
  Id: number;
  Metadata: ProductBreadcrumbMetaDataType;
} & Record<'Url' | 'Label' | 'Description', string> & {
    [key: string]: unknown;
  };

export type ProductBreadcrumbMetaDataType = [];

export type ProductSeoType = { CurrentUrl: string; CanonicalUrl: string };

export type ProductType = {
  Id: number;
  Url: string;
  HierarchyId: number;
  BrandHierarchyUrl?: string;
  Attributes: SelectedProductAttributes;
};

type OptionalProductAttributesType = Partial<
  Record<
    | 'imgAutresCurvy'
    | 'vidSlim'
    | 'vidCurvy'
    | 'imgVignetteSlim'
    | 'imgVignetteCurvy'
    | 'stickerOpeco'
    | 'noteProduit', // Used only for GTM in cart, doesn't exist to product data (use review instead)
    ProductAttributeType
  >
>;

export type ProductAttributesType = OptionalProductAttributesType &
  Record<
    | 'affichageTailleMannequin'
    | 'catalogueSKU'
    | 'codeCouleur'
    | 'couleur'
    | 'description'
    | 'gender'
    | 'idParent'
    | 'imgAutresSlim'
    | 'imgFaceCurvy'
    | 'imgDosPivot'
    | 'metaLot'
    | 'RSE'
    | 'SKUReelle',
    ProductAttributeType
  >;

export type ProductAttributeType = {
  Value: string;
};

export type ProductVariantsColorsType = Record<string, ProductVariantsSkusType>;

export type ProductVariantsSkusType = Record<string, ProductVariantType>;

export type ProductVariantType = {
  Price: string;
  Attributes: ProductVariantsAttributesType;
} & Omit<ProductType, 'Attributes'>;

type OptionalProductVariantsAttributesType = Partial<
  Record<
    | 'imgAutresCurvy'
    | 'vidSlim'
    | 'vidCurvy'
    | 'imgVignetteSlim'
    | 'imgVignetteCurvy'
    | 'stickerOpeco'
    | 'noteProduit'
    | 'prixVolume'
    | 'PRIXROUGE'
    | 'bonnet',
    ProductAttributeType
  >
>;

export type ProductVariantsAttributesType = OptionalProductVariantsAttributesType &
  Record<
    | 'catalogueSKU'
    | 'codeFamilleSsfamille'
    | 'codeLibelleFamille'
    | 'codeLibelleSsfamille'
    | 'codeTaille'
    | 'couleur'
    | 'couleurStandard'
    | 'decote'
    | 'prixUnique'
    | 'dimension'
    | 'disponibilite'
    | 'EAN'
    | 'ecopart'
    | 'estGrandeTaille'
    | 'fPMetaDescription'
    | 'fPTitle'
    | 'idAvisverifie'
    | 'idCustColor'
    | 'libelle'
    | 'libelleTaille'
    | 'metaMarque'
    | 'MORPHOPRIORITY'
    | 'nomModele'
    | 'noteProduit' // Used only for GTM in cart, doesn't exist to product data (use review instead)
    | 'review'
    | 'imgFacePivot'
    | 'imgPastillePivot'
    | 'prix'
    | 'prixSiReduction'
    | 'prixBarre'
    | 'sizeGuide'
    | 'SKUReelle'
    | 'stickerOpeco'
    | 'stickerPrincipal'
    | 'stickerSecondaire1'
    | 'stickerSecondaire2'
    | 'subuniverse'
    | 'universe'
    | 'imgFaceSlim'
    | 'imgAutresSlim'
    | 'imgFaceCurvy',
    ProductAttributeType
  >;

export type ProductColorsType = {
  couleur: string;
  couleurStandard: string;
  pastille: string;
  url: string;
};

export type ProductStickersType = {
  commercialOperation?: string;
  principal?: string;
  secondary1?: string;
  secondary2?: string;
};

export type SelectedProductAttributes = ProductAttributesType &
  ProductVariantsAttributesType;

export type FormattedProductDataType = ProductStaticDataType & {
  productAttributes: SelectedProductAttributes;
  variantsAttributes?: ProductVariantsSkusType;
  colors: ProductColorsType[];
  productType: ProductKind;
};

export type ProductDynamicDataType = {
  seo: ProductSeoType;
  relatedProducts: { products: ProductDynamicAttributesType[] } | undefined;
  similarProducts: { products: ProductDynamicAttributesType[] } | undefined;
  linkedProducts: { products: ProductDynamicAttributesType[] } | undefined;
  lastVisitedProducts: { products: ProductDynamicAttributesType[] } | undefined;
  reassurance: { htmlContent: string } | undefined;
  offer: { htmlContent: string } | undefined;
};

export type FormattedProductDynamicDataType = {
  seo?: ProductSeoType;
  relatedProducts: ProductDynamicAttributesType[];
  similarProducts: ProductDynamicAttributesType[];
  linkedProducts: ProductDynamicAttributesType[];
  lastVisitedProducts: ProductDynamicAttributesType[];
  reassurance: string | undefined;
  offer: string | undefined;
};

export const PRODUCT_TYPE = {
  PRODUCT_UNIQUE: 0,
  PRODUCT_WITH_SIZE: 1,
  PRODUCT_WITH_CUPS: 2,
} as const;

export type ProductKind = typeof PRODUCT_TYPE[keyof typeof PRODUCT_TYPE];

export type MorphologyType = typeof Morphologies[keyof typeof Morphologies];
